<template>
  <template v-if="!isLogged">
    <div class="c-heading c-heading__title-container u-mbxs">
      <h2 class="c-heading__title">{{ t('externalVersion') }}</h2>
    </div>
    <!-- <div class="c-version__login-button u-mts u-mbs">
      <a :href="loginURL" class="c-version__assets-download-button">
        <button type="button" class="c-button c-button--large">
          <span class="c-button__label u-mrs">{{ t('login') }}</span>
          <i class="c-version__assets-download-icon fa fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
        </button>
      </a>
    </div> -->
  </template>
  <div ref="projectExternalVersionsContainer" class="c-project__versions-component c-project__external-versions-component">
    <div class="c-heading c-heading__title-container u-mbxs">
      <h2 v-if="isLogged" class="c-heading__title">{{ t('externalVersion') }}</h2>
      <div v-if="project.permissions.edit">
        <div v-if="isEditExternalVersion" class="c-project__icon c-project__icon-cancel cancel-icon-versions" @click="toggleExternalVersionForm(true)">
          <SVGIcon class="c-button__icon" name="cancel-black" />
        </div>
        <!-- <div v-if="isEditVersions" class="c-project__icon c-project__icon-confirm confirm-icon-versions" @click="toggleVersionForm"><span class="fa-solid fa-sm fa-check" /></div> -->
        <div v-if="!isEditExternalVersion" class="c-project__icon c-project__icon-edit edit-icon-versions" @click="toggleExternalVersionForm(true)">
          <SVGIcon class="c-button__icon" name="pen" />
        </div>
      </div>
    </div>
    <template v-if="isEditExternalVersion && isNewExternalVersion && project.permissions.edit">
      <!-- Create external version form -->
      <ProjectExternalVersionForm
        v-model="newExternalVersion"
        :project="project"
        :error="createExternalVersion.err"
        :loading="createExternalVersion.loading"
        @submit="createExternalVersion.exec"
      />
    </template>
    <template v-if="isEditExternalVersion && !isNewExternalVersion && (externalVersion && project.permissions.edit)">
      <!-- Edit external version -->
      <template v-for="version of externalVersion">
        <ProjectExternalVersion
          v-if="version"
          :key="version"
          :version-id="version"
          :project="project"
          :is-edit-external-version="isEditExternalVersion"
          @canceled="onCancelExternalVersion"
          @updated="onUpdateExternalVersion"
          @deleted="onDeleteExternalVersion(version)"
        />
      </template>
    </template>
    <template v-if="!isEditExternalVersion && externalVersionData && externalVersion">
      <!-- External version -->
      <template v-for="version of externalVersion">
        <ProjectExternalVersion
          v-if="version && (externalVersionData.status == '1')"
          :key="version"
          :version-id="version"
          :project="project"
          @deleted="onDeleteExternalVersion(version)"
        />
      </template>
    </template>
    <template v-if="!isEditExternalVersion && (!externalVersion.length && project.permissions.edit)">
      <p class="u-color-disabled u-mts">
        <small>{{ !isEditExternalVersion && project.permissions.edit ?
          t('createFirstExternalVersion') :
          ''
        }}</small>
      </p>
    </template>
    <template v-if="!externalVersion.length && !project.permissions.edit">
      <p class="u-color-disabled u-mts">
        <small>{{ t('noExternalVersion') }}</small>
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import { useI18n } from '@/utils/i18n'
import {
  useRequest,
  getProjectExternal,
  getProjectRepo,
  editProjectExternalUrl,
  SelectedTopic,
  selectedTopicToTopicField,
  createProjectExternalVersion,
  getProjectExternalVersion,
  ExternalVersion
} from '@/utils/api'
// import { IRCAM_AUTH_URL } from '@/utils/env'
import { useAuth } from '@/utils/auth'
// import { useAuthURL } from '@/utils/urls'
import { sortPks } from '@/utils/sort'
import ProjectExternalVersion from '@/components/ProjectExternalVersion.vue'
import ProjectExternalVersionForm, { ExternalVersionForm } from '@/components/ProjectExternalVersionForm.vue'
import SVGIcon from '@/components/SVGIcon.vue'

const props = defineProps<{
  projectId: number
  projectDocumentation?: string
  requestUser: string
  isAuthenticated: boolean
}>()

const emit = defineEmits<{
  (e: 'rerender'): void
}>()

const project = ref(await getProjectExternal(props.projectId))
await project.value.releases.sort(sortPks)
console.log('PROJECT: ', project.value)
// console.log('PROJECT RELEASES SORTED: ', project.value.releases)

const projectRepository = ref(await getProjectRepo(project.value.pk, false))
// console.log('PROJECT REPOSITORY: ', projectRepository.value)

// const fetchProject = async () => {
//   // get existing project
//   const p = await getProject(projectId.value)
//   // get project's custom links
//   let customLinks = await getProjectCustomDownloadLinks(projectId.value)
//   // set a default link
//   if (customLinks.length === 0) {
//     customLinks = [ { title: 'Custom Download Link', url: '' } ]
//   }
//   if (p.configuration) {
//     p.configuration.download_behavior.strategies.custom_links.links = customLinks
//   }
//   return p
// }

// const project = ref(await fetchProject())
// console.log(project.value)

const externalVersion = ref<number[]>(project.value.releases.map(r => r.pk))
// console.log('EXTERNAL VERSION: ', externalVersion.value)

const externalVersionData = ref<ExternalVersion>()
if (externalVersion.value.length) {
  externalVersionData.value = await getProjectExternalVersion(externalVersion.value[0])
}
// console.log('EXTERNAL VERSION DATA: ', externalVersionData.value)

const projectExternalVersionsContainer = ref<HTMLElement>()
const projectGeneralContainer = ref(document.querySelector('.c-project__general-container'))

// const isSameUser = ref(project.value.owner.username === props.requestUser)
// const isAuthenticated = ref(props.isAuthenticated)

const { isLogged } = useAuth()
// const { loginURL } = useAuthURL()

const isEditExternalVersion = ref(false)
const isNewExternalVersion = ref(false)

// using a SelectedTopic ref to handle tags input
const topics = ref<SelectedTopic[]>([])
watchEffect(() => {
  topics.value = project.value.topics.map(t => ({
    id: t.id.toString(),
    text: t.name,
    create: false
  }))
})

function toggleExternalVersionForm (isFirstVersion?: boolean) {
  isEditExternalVersion.value = !isEditExternalVersion.value
  // console.log('isEditExternalVersion: ', isEditExternalVersion.value)

  // console.log('isFirstVersion: ', isFirstVersion)

  if (isFirstVersion) {
    if (!externalVersion.value.length) {
      isNewExternalVersion.value = true
    } else {
      isNewExternalVersion.value = false
    }
  } else if (!isFirstVersion) {
    isEditExternalVersion.value = true
    isNewExternalVersion.value = true
  }

  if (!isEditExternalVersion.value) {
    emit('rerender')
  }
}

// create version
const initialNewExternalVersion = () => ({
  name: '',
  description: '',
  versionNumber: '',
  datePublished: '',
  status: '1',
  externalUrl: project.value.external_url,
  externalGitRepository: project.value.external_git_repository
})

const newExternalVersion = ref<ExternalVersionForm>(initialNewExternalVersion())

const createExternalVersion = useRequest(async () => {
  // Reformat date to YYYY-MM-DDT00:00:00+02:00
  // let dateReformatted

  if (newExternalVersion.value.datePublished) {
    // dateReformatted = newExternalVersion.value.datePublished.replaceAll('/', '-').concat('T00:00:00+02:00')
  } else {
    newExternalVersion.value.datePublished = new Date().toISOString().split('T')[0]
    // dateReformatted = new Date().toISOString().split('T')[0]
  }

  const created = await createProjectExternalVersion({
    name: newExternalVersion.value.name,
    description: newExternalVersion.value.description,
    version_number: newExternalVersion.value.versionNumber,
    date_published: newExternalVersion.value.datePublished,
    status: newExternalVersion.value.status,
    external_url: newExternalVersion.value.externalUrl,
    external_git_repository: newExternalVersion.value.externalGitRepository,
    assets: [],
    release_notes: '',
    project_id: project.value.pk.toString()
  })
  // update project external url
  // const config = project.value.configuration

  const externalUrlBody = {
    title: project.value.title,
    description: project.value.description,
    topics: topics.value.map(t => selectedTopicToTopicField(t)),
    status: project.value.status,
    // configuration: config,
    external_url: newExternalVersion.value.externalUrl,
    external_git_repository: newExternalVersion.value.externalGitRepository
  }
  await editProjectExternalUrl(props.projectId, externalUrlBody)

  if (newExternalVersion.value.externalGitRepository) {
    // reload in order to update repository content
    document.location.reload()
  } else {
    // update project value
    project.value = await getProjectExternal(props.projectId)
    // reset form
    newExternalVersion.value = initialNewExternalVersion()
    // add to external version array
    externalVersion.value.push(created.release_pk)

    // update to rerender
    onUpdateExternalVersion()

    // // toggle edit form
    // isEditExternalVersion.value = false
    // // scroll to versions container
    // if (projectExternalVersionsContainer.value) {
    //   projectExternalVersionsContainer.value.scrollIntoView({ behavior: 'smooth' })
    // }
  }
})

// function checkLinkProtocol (text: string) {
//   if (text.includes('http')) {
//     return text
//   } else {
//     const linkText = 'http://' + text
//     return linkText
//   }
// }

function onCancelExternalVersion () {
  // // toggle version form
  // isEditExternalVersion.value = false

  // if (projectExternalVersionsContainer.value) {
  //   projectExternalVersionsContainer.value.scrollIntoView({ behavior: 'smooth' })
  // }

  // toggle version form
  isEditExternalVersion.value = false

  emit('rerender')

  if (projectGeneralContainer.value) {
    projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
  }
}

async function onUpdateExternalVersion () {
  if (isNewExternalVersion.value) {
    // update project
    project.value = await getProjectExternal(props.projectId)
    console.log('updated project: ', project.value)

    // toggle version form
    isEditExternalVersion.value = false

    emit('rerender')

    if (projectGeneralContainer.value) {
      projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
    }
  } else {
    // reload in order to update repository content
    document.location.reload()
  }
}

async function onDeleteExternalVersion (versionId: number) {
  projectRepository.value = await getProjectRepo(project.value.pk, false)

  if (!projectRepository.value) {
    // update project
    project.value = await getProjectExternal(props.projectId)

    // update versions
    externalVersion.value = project.value.releases.map(r => r.pk)
    // remove version from versions array
    externalVersion.value = externalVersion.value.filter(r => r !== versionId)

    // toggle version form
    isEditExternalVersion.value = false

    emit('rerender')

    if (projectGeneralContainer.value) {
      projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
    }
  } else {
    // reload in order to update repository content
    document.location.reload()
  }
}

// const submit = useRequest(async () => {
//   // const body = await makeBody()
//   // const data = await createProject(body)
//   // // redirect user to project page
//   // window.location.href = data.forum_project_url
//   // isRedirecting.value = true
//   // return data
// }, {
//   error () {
//     // scroll to error message when an error occurs
//     if (form.value) {
//       form.value.scrollIntoView({ behavior: 'smooth' })
//     }
//   }
// })

const { t } = useI18n({
  messages: {
    en: {
      createFirstExternalVersion: 'Add a version by clicking the edit button above.',
      noExternalVersion: "There aren't any versions yet.",
      externalVersion: 'Version',
      login: 'Login'
    },
    fr: {
      createFirstExternalVersion: ' Ajouter une version en cliquant sur le bouton d\'édition ci-dessus.',
      noExternalVersion: 'Aucune version pour le moment.',
      externalVersion: 'Version',
      login: 'Se connecter'
    }
  }
})
</script>
